import { useNavigate, useParams, Link } from "react-router-dom";
import { PageLoader } from "#/components/common/PageLoader";
import { ErrorState } from "#/components/common/ErrorState";
import { RoleForm } from "../components/RoleForm";
import { usePermissionOptions, useRole, useUpdateRole } from "../hooks";
import type { RoleFormValues } from "../types";
import { ArrowLeft } from "lucide-react";

export default function RoleEditPage() {
  const navigate = useNavigate();
  const { roleId } = useParams();
  const id = Number(roleId);

  const roleQuery = useRole(id);
  const permissionsQuery = usePermissionOptions();
  const updateRole = useUpdateRole();

  if (!Number.isFinite(id) || id <= 0) {
    return (
      <ErrorState
        title="Invalid role"
        message="The selected role id is invalid."
      />
    );
  }

  if (roleQuery.isLoading || permissionsQuery.isLoading) {
    return <PageLoader label="Loading role..." />;
  }

  if (roleQuery.isError || !roleQuery.data) {
    return (
      <ErrorState
        title="Failed to load role"
        message={
          roleQuery.error instanceof Error
            ? roleQuery.error.message
            : "Could not load the selected role."
        }
      />
    );
  }

  if (permissionsQuery.isError) {
    return (
      <ErrorState
        title="Failed to load permissions"
        message={
          permissionsQuery.error instanceof Error
            ? permissionsQuery.error.message
            : "Could not load permission options."
        }
      />
    );
  }

  const role = roleQuery.data;

  const defaultValues: RoleFormValues = {
    name: role.name,
    code: role.code,
    description: role.description ?? "",
    active: role.active,
    permissionCodes: role.permissionCodes ?? [],
  };

  async function handleSubmit(values: RoleFormValues) {
    await updateRole.mutateAsync({
      id,
      payload: {
        name: values.name,
        description: values.description,
        active: values.active,
        permissionCodes: values.permissionCodes,
      },
    });

    navigate(`/admin/roles/${id}`);
  }

  return (
    <div className="space-y-6">
      <div>
        <button
          onClick={() => navigate(-1)}
          className="inline-flex items-center gap-2 text-sm font-semibold text-emerald-700 hover:underline"
        >
          <ArrowLeft className="h-4 w-4" />
          Go back
        </button>

        <h1 className="mt-3 text-2xl font-bold text-slate-900">Edit role</h1>
        <p className="text-sm text-slate-500">
          Update role details and assigned permissions.
        </p>
      </div>

      <RoleForm
        defaultValues={defaultValues}
        permissions={permissionsQuery.data ?? []}
        isSubmitting={updateRole.isPending}
        onSubmit={handleSubmit}
      />
    </div>
  );
}
